import { useCallback, useEffect, useMemo, useState } from 'react'
import { useI18n } from '../../i18n'
import { homeApi } from '../../services/api'
import { fetchEnterpriseHomeSummary, type EnterpriseHomeSummary } from '../../services/enterpriseOperationalUxApi'
import {
  buildHomeComplianceFromSnapshots,
  type HomeComplianceStandard,
} from '../../utils/homeComplianceDisplay'
import { getStandardDisplayName, normalizeComplianceStandardCode } from '../../utils/complianceStandard'
import type { EffectiveStandardAccess } from '../../services/standardAccessApi'
import HomeStandardExecutiveCard from '../enterprise-operational/HomeStandardExecutiveCard'
import styles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'

type Props = {
  standardCodes: string[]
  effectiveAccess?: EffectiveStandardAccess | null
}

function toHomeFrameworkName(code: string): string {
  const u = String(code || '').toUpperCase()
  if (u === 'ISO27001' || u === 'ISO_27001') return 'ISO 27001'
  if (u === 'ISO42001' || u === 'ISO_42001') return 'ISO/IEC 42001:2023'
  if (u === 'SOC2' || u === 'SOC_2' || u === 'SOC 2') return 'SOC2'
  if (u === 'NIS2' || u === 'NIS2_FULL') return 'NIS2'
  if (u === 'PCI_DSS_2025') return 'PCI DSS 2025'
  if (u === 'SWIFT_CSCF_2025') return 'SWIFT CSCF 2025'
  if (u === 'TECH_CONTROLS') return 'Technical Controls'
  if (u === 'SDLC_SECURITY') return 'SDLC Security'
  return code
}

function frameworkMatchesCode(framework: string, code: string, displayName: string): boolean {
  const f = String(framework || '').trim()
  const c = normalizeComplianceStandardCode(code)
  if (!f || !c) return false
  if (f.toUpperCase() === c.toUpperCase()) return true
  if (f === displayName) return true
  if (f === toHomeFrameworkName(c)) return true
  return false
}

export default function ComplianceCenterStandardExecutiveCards({
  standardCodes,
  effectiveAccess,
}: Props) {
  const { t } = useI18n()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [complianceByCode, setComplianceByCode] = useState<Map<string, HomeComplianceStandard>>(
    new Map(),
  )
  const [summary, setSummary] = useState<EnterpriseHomeSummary | null>(null)

  const normalizedCodes = useMemo(
    () =>
      Array.from(
        new Set(standardCodes.map((c) => normalizeComplianceStandardCode(c)).filter(Boolean)),
      ),
    [standardCodes.join(',')],
  )

  const load = useCallback(async () => {
    if (!normalizedCodes.length) {
      setComplianceByCode(new Map())
      setSummary(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const [snapshotsRes, enterpriseSummary] = await Promise.all([
        homeApi.getUnifiedAssessmentSnapshots(normalizedCodes),
        fetchEnterpriseHomeSummary(normalizedCodes),
      ])
      const snapshots = Array.isArray(snapshotsRes?.standards) ? snapshotsRes.standards : []
      const complianceRows = buildHomeComplianceFromSnapshots(
        snapshots,
        toHomeFrameworkName,
        (key, fallback) => t(key, fallback),
      )
      const byCode = new Map<string, HomeComplianceStandard>()
      for (const row of complianceRows) {
        if (row.standardCode) byCode.set(row.standardCode, row)
      }
      for (const code of normalizedCodes) {
        if (!byCode.has(code)) {
          byCode.set(code, {
            name: getStandardDisplayName(t, code),
            standardCode: code,
            selfPercent: null,
            verifiedPercent: null,
            percent: 0,
            statusKey: 'notStarted',
            statusLabel: t('standardGovernance.notSelfAssessed', 'Not self-assessed'),
            scopeSummary: t('standardGovernance.scopeOrganizationWide', 'Organization-wide'),
            total: 0,
            answered: 0,
            distribution: null,
          })
        }
      }
      setComplianceByCode(byCode)
      setSummary(enterpriseSummary)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load standard cards')
      setComplianceByCode(new Map())
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }, [normalizedCodes.join(','), t])

  useEffect(() => {
    void load()
  }, [load])

  const executiveByCode = useMemo(() => {
    const map = new Map<string, NonNullable<EnterpriseHomeSummary['standards']>[number]>()
    for (const card of summary?.standards || []) {
      map.set(card.standardCode, card)
    }
    return map
  }, [summary?.standards])

  if (!normalizedCodes.length) return null

  return (
    <div data-testid="cc-standard-executive-cards">
      {loading ? (
        <p className={styles.emptyState}>{t('common.loading', 'Loading…')}</p>
      ) : null}
      {error ? (
        <p className={styles.emptyState} role="alert">
          {error}
        </p>
      ) : null}
      {!loading && !error ? (
        <div className={styles.standardGrid}>
          {normalizedCodes.map((code) => {
            const compliance = complianceByCode.get(code)
            if (!compliance) return null
            return (
              <HomeStandardExecutiveCard
                key={code}
                compliance={compliance}
                executive={executiveByCode.get(code) ?? null}
                scopeLabel={summary?.scopeByStandard?.[code]?.label}
                assetCount={summary?.organizationAssetCount}
                effectiveAccess={effectiveAccess}
              />
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

export { frameworkMatchesCode, toHomeFrameworkName }
